import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  PenLine,
  FileSearch,
  FileText,
  Send,
  ChevronRight,
  ChevronLeft,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    draft: 0,
    pending: 0,
    scheduled: 0,
    published: 0,
    rejected: 0,
  });
  const [recentPRs, setRecentPRs] = useState([]);
  const [attentionNeeded, setAttentionNeeded] = useState([]);
  const [scheduledPRs, setScheduledPRs] = useState([]);
  const [attentionIndex, setAttentionIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, recentRes, attentionRes, scheduledRes] = await Promise.all([
        fetch(`${API_URL}/api/press-releases/stats`, { credentials: "include" }),
        fetch(`${API_URL}/api/press-releases/recent?limit=15`, { credentials: "include" }),
        fetch(`${API_URL}/api/press-releases/attention`, { credentials: "include" }),
        fetch(`${API_URL}/api/press-releases/scheduled`, { credentials: "include" }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (recentRes.ok) setRecentPRs(await recentRes.json());
      if (attentionRes.ok) setAttentionNeeded(await attentionRes.json());
      if (scheduledRes.ok) setScheduledPRs(await scheduledRes.json());
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Submit a press release",
      icon: PenLine,
      action: () => navigate("/submit-pr"),
    },
    {
      title: "Check press release status",
      icon: FileSearch,
      action: () => navigate("/manage-pr"),
    },
    {
      title: "White label case study",
      icon: FileText,
      action: () => navigate("/resources"),
    },
    {
      title: "Learn about PR distribution",
      icon: Send,
      action: () => navigate("/resources"),
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      pending: "bg-amber-50 text-amber-700",
      scheduled: "bg-blue-50 text-blue-700",
      published: "bg-emerald-50 text-emerald-700",
      rejected: "bg-red-50 text-red-700",
      needs_revision: "bg-orange-50 text-orange-700",
    };
    return styles[status] || styles.draft;
  };

  const formatRelativeTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto animate-fade-in">
      {/* Promo Banner */}
      <Card className="bg-slate-900 border-0 text-white overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-blue-600 rounded-full px-4 py-2 font-bold text-sm">
                USA TODAY
              </div>
              <div>
                <p className="font-semibold flex items-center gap-2">
                  <span className="bg-emerald-500 text-xs px-2 py-0.5 rounded">NEW</span>
                  USA TODAY just joined PR Link Boost 🎉
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  Publish on a DR 92 outlet with our 37% launch special. Auto-included with Executive and Director plans.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="bg-transparent border-slate-600 text-white hover:bg-slate-800"
              data-testid="promo-cta-btn"
            >
              Publish on USA TODAY
              <span className="text-xs ml-2 text-slate-400">Limited-time promo</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Welcome Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">
            Welcome, {user?.name?.split(" ")[0] || "User"}
          </h1>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-slate-600">
            What would you like to do today?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all text-left group"
                data-testid={`quick-action-${index}`}
              >
                <action.icon className="h-5 w-5 text-slate-400 group-hover:text-slate-600" />
                <span className="text-sm font-medium text-slate-700">{action.title}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stats and Attention Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats Overview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Press Release Overview */}
          <div>
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
              Press Release Overview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: "Draft", value: stats.draft, color: "text-slate-700" },
                { label: "Pending", value: stats.pending, color: "text-amber-600" },
                { label: "Scheduled", value: stats.scheduled, color: "text-blue-600" },
                { label: "Published", value: stats.published, color: "text-emerald-600" },
                { label: "Rejected", value: stats.rejected + (stats.needs_revision || 0), color: "text-red-600", highlight: true },
              ].map((stat, index) => (
                <Card
                  key={index}
                  className={`${stat.highlight ? "border-red-200 bg-red-50/30" : ""}`}
                  data-testid={`stat-${stat.label.toLowerCase()}`}
                >
                  <CardContent className="p-4 text-center">
                    <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                    <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Upcoming Publication */}
          <div>
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
              Upcoming Publication
            </h2>
            <Card>
              <CardContent className="p-4">
                {scheduledPRs.length > 0 ? (
                  <div className="space-y-3">
                    {scheduledPRs.slice(0, 3).map((pr) => (
                      <div key={pr.pr_id} className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-slate-600">
                          {format(new Date(pr.scheduled_date), "MMM d, yyyy")}
                        </span>
                        <span className="text-sm text-slate-900 truncate flex-1">{pr.title}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No scheduled releases.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Press Releases */}
          <div>
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">
              Recent Press Release
            </h2>
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {recentPRs.slice(0, 12).map((pr) => (
                    <div
                      key={pr.pr_id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/edit-pr/${pr.pr_id}`)}
                      data-testid={`recent-pr-${pr.pr_id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <span className="text-sm text-slate-400 w-24 flex-shrink-0">
                          {formatRelativeTime(pr.created_at)}
                        </span>
                        <span className="text-sm text-slate-900 truncate">{pr.title}</span>
                      </div>
                      <Badge className={`${getStatusBadge(pr.status)} capitalize ml-4`}>
                        {pr.status.replace("_", " ")}
                      </Badge>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-slate-100">
                  <Button
                    variant="ghost"
                    className="text-slate-600 hover:text-slate-900"
                    onClick={() => navigate("/manage-pr")}
                    data-testid="view-all-prs-btn"
                  >
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Attention Needed Panel */}
        <div>
          {attentionNeeded.length > 0 && (
            <Card className="border-red-200 sticky top-24">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <CardTitle className="text-red-600 text-lg">Attention Needed</CardTitle>
                    <Badge className="bg-blue-600 text-white">{attentionNeeded.length}</Badge>
                  </div>
                  {attentionNeeded.length > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setAttentionIndex(Math.max(0, attentionIndex - 1))}
                        disabled={attentionIndex === 0}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-slate-500">
                        {attentionIndex + 1} of {attentionNeeded.length}
                      </span>
                      <button
                        onClick={() =>
                          setAttentionIndex(Math.min(attentionNeeded.length - 1, attentionIndex + 1))
                        }
                        disabled={attentionIndex === attentionNeeded.length - 1}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {attentionNeeded[attentionIndex] && (
                  <div className="space-y-4">
                    <p className="text-sm text-red-600">
                      Rejected on{" "}
                      {format(
                        new Date(attentionNeeded[attentionIndex].rejection_date),
                        "d MMM, yy, hh:mma"
                      )}
                    </p>
                    <h3 className="font-semibold text-slate-900">
                      {attentionNeeded[attentionIndex].title}
                    </h3>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm text-slate-500 mb-1">Others:</p>
                      <p className="text-sm text-slate-700">
                        {attentionNeeded[attentionIndex].rejection_reason ||
                          "Please review and revise your press release."}
                      </p>
                    </div>
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() =>
                        navigate(`/edit-pr/${attentionNeeded[attentionIndex].pr_id}`)
                      }
                      data-testid="revise-pr-btn"
                    >
                      Revise
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
